import { Button } from "@mui/material";
import React from "react";

import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMessageFormActions } from "hooks/useMessageFormActions";

import { useSendChargeScheduleMessageListMutation } from "src/store/api/generatedApi";
import { deployPlanFormSlice } from "src/store/slices/deployPlanFormSlice";
import { useAppDispatch, useAppStore } from "src/store/store";

import { typeSafeObjectEntries } from "src/utils/typeSafeObjectEntries";
import { PlanId } from "components/common/boundary/PlanId";

interface Props {
  formPlanId: PlanId;
}

export const SendMessageButton = ({ formPlanId }: Props) => {
  const { t } = useTenantTranslation();
  const dispatch = useAppDispatch();
  const tenantName = useTenant();
  const store = useAppStore();
  const notifyStatus = useNotifyStatus();
  const { messageType, periodMessagesSelector, backupMessagesSelector } =
    useMessageFormActions(formPlanId);

  const [send, { isLoading }] = useSendChargeScheduleMessageListMutation();

  const handleOnClick = React.useCallback(async () => {
    const periodMessages = periodMessagesSelector(store.getState(), formPlanId);
    const backupPlanMessages = backupMessagesSelector(
      store.getState(),
      formPlanId
    );

    if (periodMessages && backupPlanMessages) {
      try {
        await send({
          tenantName,
          chargeScheduleMessages: {
            charge_schedule_messages: [
              ...typeSafeObjectEntries(periodMessages).map(
                ([period, message]) => ({
                  plan_id: formPlanId,
                  period: period,
                  text: message,
                })
              ),
              ...typeSafeObjectEntries(backupPlanMessages).map(
                ([planId, message]) => ({
                  plan_id: planId,
                  period: 1,
                  text: message,
                })
              ),
            ],
          },
        }).unwrap();
        dispatch(deployPlanFormSlice.actions.setFinishedForm(formPlanId));
      } catch {
        notifyStatus({ type: "error", text: t(`${messageType}_error`) });
      }
    }
  }, [
    send,
    store,
    notifyStatus,
    t,
    formPlanId,
    messageType,
    periodMessagesSelector,
    backupMessagesSelector,
    dispatch,
    tenantName,
  ]);

  return (
    <Button
      variant="contained"
      loading={isLoading}
      onClick={() => void handleOnClick()}
    >
      {t(`send_${messageType}`)}
    </Button>
  );
};
